import { Button, Empty, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getUserInfo } from '../../../../utils/auth';
import ParcelDetailDrawer from '../../../delivery/parcels/components/ParcelDetailDrawer';
import { formatBatchTimeSeconds } from '../../sorting-batch/utils/formatUtils';
import type { BagBatchRecordType, BagBatchWaybillType } from '../graphql/queries/getBagBatchRecords';

const SEG = 'wms.sortingBatch.bagDetail.segment';

const formatShortTime = (time: number): string => dayjs(time).format('MM/DD HH:mm');

/** 运单表每页条数：合包后的大包可达上千件，不分页一次渲染太重 */
const WAYBILL_PAGE_SIZE = 20;

/** 整包移入、单独移入都展示为「移入批次」；整包移入另在加入方式后标注原批次保留 */
const joinWayKeyOf = (record: BagBatchRecordType): 'CREATED' | 'MOVED_IN' => (record.joinWay === 'CREATED' ? 'CREATED' : 'MOVED_IN');

/** 只给已移出的段打标；仍在批次的段不打标（跨仓整包移入时上游批次保留，会有多段同时在批次） */
const SegmentTag: React.FC<{ record: BagBatchRecordType }> = ({ record }) => {
    const { t } = useTranslation();
    if (record.leftAt === null) return null;
    return <span className="flex-none rounded bg-[#f2f3f5] px-1.5 text-[11.5px] text-[#86909c]">{t(`${SEG}.movedOut`)}</span>;
};

interface BagBatchSegmentListProps {
    /** 按加入时间升序 */
    records: BagBatchRecordType[];
    selectedIndex: number;
    onSelect: (index: number) => void;
}

/** 包牌详情「批次记录」Tab 左侧：包牌待过的每个批次段 */
export const BagBatchSegmentList: React.FC<BagBatchSegmentListProps> = ({ records, selectedIndex, onSelect }) => {
    const { t } = useTranslation();
    if (!records.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t(`${SEG}.empty`)} />;
    // 上游批次说明只对整包移入的包牌有意义：这类包牌同时还在上游仓批次里
    const hasMovedWithBag = records.some((record) => record.joinWay === 'MOVED_WITH_BAG');
    return (
        <div className="flex flex-col gap-2">
            {records.map((record, i) => (
                <button
                    type="button"
                    key={`${record.batchId}-${record.joinedAt}`}
                    data-testid="bag-segment-card"
                    onClick={() => onSelect(i)}
                    className={`w-full cursor-pointer rounded border border-solid px-3 py-2.5 text-left ${i === selectedIndex ? 'border-ant-purple-6 bg-[#faf7ff]' : 'border-[#e5e6eb] bg-white'}`}
                >
                    <div className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-[#1d2129]">{record.batchName}</span>
                        <SegmentTag record={record} />
                    </div>
                    <div className="mt-1 text-xs leading-[1.7] text-[#86909c]">
                        {record.siteName || '-'} · {t(`${SEG}.joinWay.${joinWayKeyOf(record)}`)}
                    </div>
                    <div className="text-xs leading-[1.7] text-[#86909c]">
                        {t(`${SEG}.joined`, { time: formatShortTime(Number(record.joinedAt)) })}
                        {record.leftAt !== null && ` → ${t(`${SEG}.left`, { time: formatShortTime(Number(record.leftAt)) })}`}
                    </div>
                </button>
            ))}
            {hasMovedWithBag && <div className="text-xs leading-[1.7] text-[#86909c]">{t(`${SEG}.note`)}</div>}
        </div>
    );
};

/** 包牌详情「批次记录」Tab 右侧：选中批次段的运单（同原型只展示运单号、计入时间；点运单号打开包裹详情） */
export const BagBatchSegmentDetail: React.FC<{ record?: BagBatchRecordType }> = ({ record }) => {
    const { t } = useTranslation();
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
    if (!record) return null;

    const columns: ColumnsType<BagBatchWaybillType> = [
        {
            title: t('wms.sortingBatch.bagDetail.columns.waybillNumber'),
            dataIndex: 'trackingNumber',
            // 同包裹列表：点运单号打开包裹详情
            render: (v: string, waybill) => (
                <Button type="link" onClick={() => setSelectedOrderId(waybill.orderId)} style={{ padding: 0, height: 'auto' }}>
                    {v}
                </Button>
            ),
        },
        {
            title: t('wms.sortingBatch.bagDetail.columns.joinAt'),
            dataIndex: 'joinedAt',
            width: 170,
            render: (v: string) => <span className="text-[#4e5969]">{formatBatchTimeSeconds(Number(v))}</span>,
        },
    ];

    return (
        <>
            <div className="mb-3 flex flex-wrap items-center gap-2.5">
                <span className="text-sm font-medium">{t(`${SEG}.title`)}</span>
                <span className="text-[13px] font-medium text-ant-purple-6">{record.batchName}</span>
                <SegmentTag record={record} />
            </div>
            <Table<BagBatchWaybillType>
                // 切换批次段时回到第一页
                key={`${record.batchId}-${record.joinedAt}`}
                size="small"
                bordered
                rowKey="trackingNumber"
                pagination={{ pageSize: WAYBILL_PAGE_SIZE, size: 'small', showSizeChanger: false, hideOnSinglePage: true }}
                columns={columns}
                dataSource={record.waybills}
                // 包牌移出批次时该批次的运单记录随之移出，已移出的段一般没有运单
                locale={record.leftAt !== null ? { emptyText: t(`${SEG}.leftEmpty`) } : undefined}
            />
            <div className="flex justify-end px-0.5 py-3.5 text-[13px] text-[#4e5969]">
                {t('wms.sortingBatch.bagDetail.totalRows', { count: record.waybills.length })}
            </div>
            {selectedOrderId && (
                <ParcelDetailDrawer
                    visible
                    onClose={() => setSelectedOrderId(null)}
                    orderId={selectedOrderId}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                />
            )}
        </>
    );
};
